import { BehaviorSubject } from "rxjs"
import { Filter, FilterUtil } from "src/framework/commons";
import { IDataRequest, IDataResponse, IHttpClient, STATUS_CODE } from "src/framework/providers/api";
import FileDownload from "js-file-download";
import { ResourcePaths } from "src/framework/store/DataGridStore";

type SaveDefinition = { 
    row: any,
    primaryKey?: number | string,
}

type FindDefinition = {
    primaryKey?: number | string, 
    includes: string[],
}

interface IDataGridStore<T = any> {
    list: () => Promise<void>;
    save: (saveDef: SaveDefinition) => Promise<IDataResponse<T>>;
    find: (findDef: FindDefinition) => Promise<IDataResponse<T>>;
    delete: (primaryKey: number | string) => Promise<boolean>;
    export: (fileNameExport: string) => Promise<void>
}

/**
 * @description Store for the EasyDataGrid component
 */
class EasyDataGridStore<T = any> implements IDataGridStore {
    /**
     * @description Enable or disable the pagination
     * @default true
     */
    private pagination: boolean = true;

    /**
     * @description Paths for the CRUD operations
    */
    private paths: ResourcePaths = {
        create: "",
        read: "",
        update: "",
        delete: "",
        list: "",
    };

    /**
     * @description List of rows
     */
    private rows$ = new BehaviorSubject<any[]>([]);

     /**
     * @description List of aggregations
     */
    private aggregations: any = {};

    /**
     * @description Export file name
     */
    private exportFileName: string = "";

    /**
     * @description Enable or disable the export
     */
    private enableExport: boolean = false;

    /**
     * @description List of includes
     */
    private includes: string[] = [];
    
    /**
     * @description Observable for the current page number
     */
    private page$ = new BehaviorSubject<number>(1);

    /**
     * @description Observable for the current perPageList
     */
    private perPageList$ = new BehaviorSubject<number[]>([5, 10, 20, 50, 100]);

    /**
     * @description Observable for the current perPage
     */
    private perPage$ = new BehaviorSubject<number>(5);

    /**
     * @description Observable for the current total
     */
    private total$ = new BehaviorSubject<number>(0);

    /**
     * @description Observable for the current filters
     */
    private filters$ = new BehaviorSubject<{ [filterName: string]: Filter }>({});

    /**
     * @description Observable for the current orderBy
     */
    private orderBy$ = new BehaviorSubject<string[]>([]);

    /**
     * @description Initialize the EasyDataGridStore with an IHttpClient instance
     */
    constructor(private readonly httpClient: IHttpClient) { }

    /**
     * @description Set paths definition
     * @example {create: "/api/v1/users", read: "/api/v1/users/", update: "/api/v1/users", delete: "/api/v1/users", list: "/api/v1/users/list"}
     */
    public setPaths(paths: ResourcePaths) {
        this.paths = paths;
    }

    /**
     * @description Set includes definition
     * @param includes 
     */
    public setIncludes(includes: string[]) {
        this.includes = includes;
    }

    /**
     * @description Set aggregations definition
     */
    public setAggregations(aggregations: any) {
        this.aggregations = aggregations;
    }

    /**
     * @description Get aggregations definition
     */
    public getAggregations(): any {  
        return this.aggregations;
    }

    /**
     * @description Observable for the current page number
     * @returns {Observable<number>}
     */
    public get page() {
        return this.page$.asObservable();
    }

    /**
     * @description Set the current page
     * @param page 
     */
    public setPage(page: number) {
        this.page$.next(page)
    }

    /**
     * @description Get the current page
     * @returns {number}
     */
    public getPage() {
        return this.page$.getValue();
    }

    /**
     * @description Observable for the current perPageList
     * @returns {Observable<number[]>}
     */
    public get perPageList() {
        return this.perPageList$.asObservable();
    }

    /**
     * @description Set the current perPageList
     * @param perPageList 
     * @example [5, 10, 20, 50, 100]
     */
    public setPerPageList(perPageList: number[]) {
        this.perPageList$.next(perPageList)
    }

    /**
     * @description Get the current perPageList
     * @default [5, 10, 20, 50, 100]
     * @returns {number[]} example: [5, 10, 20, 50, 100]
     */
    public getPerPageList() {
        return this.perPageList$.getValue();
    }

    /**
     * @description Observable for the current perPage 
     */
    public get perPage() {
        return this.perPage$.asObservable();
    }

    /**
     * @description Set the current perPage
     * @param perPage
     * @example 5
     */
    public setPerPage(perPage: number) {
        this.perPage$.next(perPage)
    }

    /**
     * @description Get the current perPage
     * @default 5
     * @example 5
     * @returns {number}
     */
    public getPerPage() {
        return this.perPage$.getValue();
    }

    /**
     * @description Observable for the current total
     * @returns {Observable<number>}
     */
    public get total() {
        return this.total$.asObservable();
    }

    /**
     * @description Set the current total
     * @param total 
     * @example 100
     */
    public setTotal(total: number) {
        this.total$.next(total)
    }

    /**
     * @description Get the current total
     * @example 100
     * @returns {number}
     * @default 0
     */
    public getTotal() {
        return this.total$.getValue();
    }

    /**
     * @description Observable for the current filters
     * @returns {Observable<{[filterName: string]: Filter}>}
    */
    public get filters() {
        return this.filters$.asObservable();
    }

    /**
     * @description Set the current filters
     * @param filters
     * @example {name: {field: "name", criteria: SUFIXO_FIELD.LIKE, value: "John"}}
     * @example {name: {field: "name", criteria: SUFIXO_FIELD.LIKE, value: "John"}, age: {field: "age", criteria: SUFIXO_FIELD.IN, value: [12,30]}}
     */
    public setFilters(filters: { [filterName: string]: Filter }) {
        this.filters$.next(filters)
    }

    /**
     * @description Get the current filters
     * @example {name: {field: "name", criteria: SUFIXO_FIELD.LIKE, name: {value: "John"}} 
     * @example {name: {field: "name", criteria: SUFIXO_FIELD.LIKE, name: {value: "John"}, age: a{field: "age", criteria: SUFIXO_FIELD.IN, value: [12,30]}}
     * @returns {{[filterName: string]: Filter}}
     */
    public getFilters() {
        return this.filters$.getValue();
    }

    /**
     * @description Observable for the current rows
     */
    public get rows() {
        return this.rows$.asObservable();
    }


    /**
     * @description set rows filtered
     * @param rows
     */
    public setRows(rows: T[]) {
        this.rows$.next(rows)
    }

    /**
     * @description Get rows filtered
     * @returns {rows<T>}
     */
    public getRows() {
        return this.rows$.getValue();
    }

    /**
     * @description Observable for the current orderBy
     * @returns {Observable<string[]>}
     */
    public get orderBy() {
        return this.orderBy$.asObservable();
    }

    /**
     * @description Set the current orderBy
     * @param orderBy
     * @example ["name", "age"]
     * @example ["name-desc", "age"]
     * @example ["name", "age-desc"]
     * @example ["name-desc", "age-desc"]
     */
    public setOrderBy(orderBy: string[]): void {
        this.orderBy$.next(orderBy)
    }

    /**
     * @description Get the current orderBy
     * @example ["name", "age"]
     * @example ["name-desc", "age"]
     * @example ["name", "age-desc"]
     * @example ["name-desc", "age-desc"]
     * @returns {string[]}
     */
    public getOrderBy(): string[] {
        return this.orderBy$.getValue();
    }

    /**
     * @description Get the current orderBy in plain string
     * @example "&orderby=name,age"
     * @example "&orderby=name-desc,age"
     * @example "&orderby=name,age-desc"
     * @example "&orderby=name-desc,age-desc"
     * @returns {string}
     */
    private get _plainOrderBy(): string {
        return this.getOrderBy().length > 0 ? `&orderby=${this.getOrderBy().join(",")}` : "";
    }

    /**
     * 
     * @param orderBy 
     */
    public removeOrderBy(orderBy: string): void {
        const newOrder = this.getOrderBy().filter(o => o !== orderBy);
        this.setOrderBy(newOrder);
    }

    /**
     * @description Add a new orderBy
     * @param orderBy
     * @example "name"
     * @example "name-desc"
     */
    public addOrderBy(orderBy: string): void {
        const newOrder = this.getOrderBy()
        newOrder.push(orderBy);
        this.setOrderBy(newOrder);
    }


    /**
     * @description Reset the current page to 1
     */
    public reset() {
        this.page$.next(1)
    }


    /**
     * @description Check if the paths are defined
     * @param path 
     * @throws {Error} if the path is not defined
     * @example checkPaths("create")
     */
    private checkPaths(path: "create" | "read" | "update" | "delete" | "list" ) {
        switch (path) {
            case "create":
                if (!this.paths.create) {
                    throw new Error("The path for create is not defined");
                }
                break;
            case "read":
                if (!this.paths.read) {
                    throw new Error("The path for read is not defined");
                }
                break;
            case "update":
                if (!this.paths.update) {
                    throw new Error("The path for update is not defined");
                }
                break;
            case "delete":
                if (!this.paths.delete) {
                    throw new Error("The path for delete is not defined");
                }
                break;
            case "list":
                if (!this.paths.list) {
                    throw new Error("The path for list is not defined");
                }
                break;
            default:
                break;
        }
    }

    /**
     * @description 
     * @param filters 
     * @returns 
     */
    private _buildFilter(filters: {[field: string]: Filter}): string {
        return FilterUtil.buildFromObject(filters)
    }
    

    /**
     * @description Aply the filters and the order by and get list of items with pagination or not
     * @returns {Promise<void>}
     */
    public async list(): Promise<void> {
        try {
            
            this.checkPaths("list"); // check if the path is defined

            const urlOthers: string[] = [];

            // Pagination
            if (this.pagination) {
                urlOthers.push(`&page=${this.getPage()}&limit=${this.getPerPage()}`);
            }

            // Filter
            const filter = this._buildFilter(this.getFilters());
            if (filter) {
                urlOthers.push(filter);
            }

            // Includes
            if (this.includes.length) {
                urlOthers.push(`&includes=${this.includes.join(",")}`);
            }


            // Order
            const orderBy = this._plainOrderBy;
            if (orderBy) {
                urlOthers.push(this._plainOrderBy);
            }

            const request: IDataRequest = {
                url: `${this.paths.list}/${urlOthers.length ? "?" + urlOthers.join("").substring(1) : ""}`,
            }

            const response: IDataResponse = await this.httpClient.get(request);


            if (response.statusCode === STATUS_CODE.OK) {
                this.setRows([]);

                if (this.pagination) {
                    this.setRows([...(response.data?.rows || [])]);
                      this.setAggregations(response?.data?.aggregates || {})
                      this.setTotal(response.data?.infoRows?.count);
                } else {
                    this.setRows(response?.data || []);
                      this.setAggregations({});
                      this.setTotal(response?.data?.length || 0);}
            }

            if (response.statusCode !== STATUS_CODE.OK) {
                this.setRows([]);
                this.setAggregations({})
                this.setTotal(0);
            }

        } catch (error) {
            throw error;
        }
    }

    public async find({ primaryKey, includes = [] }: { primaryKey?: number | string, includes: string[] }): Promise<IDataResponse<T>> {
        try {
            this.checkPaths("read"); 

            const request: IDataRequest = {
                url: `${this.paths.read}/${primaryKey}${includes.length ? "?includes=" + includes.join(",") : ""}`,
            }

            return await this.httpClient.get(request); 
        } catch (error) {
            throw error;
        }
    }

    public async save({ row, primaryKey = "" }: { row: any, primaryKey?: number | string }): Promise<IDataResponse<T>> {
        throw new Error("Method not implemented.");
    }


    public async update({ row, primaryKey = "" }: { row: any, primaryKey?: number | string }): Promise<IDataResponse<T>> {
        throw new Error("Method not implemented.");
    }

    public async delete(primaryKey: number | string): Promise<boolean> {
        throw new Error("Method not implemented.");
    }


    public async export(fileNameExport: string): Promise<void> {
        const response = await this?.httpClient?.getFile({
            url: `${this.paths.list}?format=xls`,
        });
    
        if (response?.statusCode === STATUS_CODE.OK && response?.data) {
            FileDownload(
                response.data,
                fileNameExport,
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            );
        }
    }

}

export { EasyDataGridStore }